import { useState, useCallback } from 'react'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { showErrorToast, showSuccessToast } from '../utils/toast'

export function useWebinars() {
  const [loading, setLoading] = useState(false)

  // ============= USER/PUBLIC APIs =============

  // Lister les webinaires publiés
  const getWebinars = useCallback(async (options = {}) => {
    const { status = 'ALL', page = 1, limit = 10, language } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        status,
        page: page.toString(),
        limit: limit.toString()
      })
      if (language) {
        params.append('language', language)
      }
      const data = await api.get(`${endpoints.webinars}?${params.toString()}`, {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement des webinaires'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Détails d'un webinaire
  const getWebinar = useCallback(async (webinarId, options = {}) => {
    const { language } = options
    setLoading(true)
    try {
      let url = endpoints.getWebinar(webinarId)
      if (language) {
        url += `?language=${language}`
      }
      const data = await api.get(url, {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement du webinaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // S'inscrire à un webinaire
  const registerToWebinar = useCallback(async (webinarId) => {
    setLoading(true)
    try {
      const data = await api.post(endpoints.registerToWebinar(webinarId))
      showSuccessToast({ message: 'Inscription réussie au webinaire' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de l\'inscription au webinaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Annuler son inscription
  const cancelRegistration = useCallback(async (webinarId) => {
    setLoading(true)
    try {
      await api.delete(endpoints.cancelWebinarRegistration(webinarId))
      showSuccessToast({ message: 'Inscription annulée avec succès' })
    } catch (error) {
      const message = error?.message || 'Erreur lors de l\'annulation de l\'inscription'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Mes inscriptions aux webinaires
  const getMyRegistrations = useCallback(async (options = {}) => {
    const { status = 'ALL', page = 1, limit = 10, language } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        status,
        page: page.toString(),
        limit: limit.toString()
      })
      if (language) {
        params.append('language', language)
      }
      const data = await api.get(`${endpoints.myWebinarRegistrations}?${params.toString()}`)
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement de vos inscriptions'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // ============= ADMIN APIs =============

  // Créer un webinaire
  const createWebinar = useCallback(async (webinarData) => {
    setLoading(true)
    try {
      // Vérifier si un fichier thumbnail est présent
      const hasThumbnailFile = webinarData.thumbnailFile instanceof File || webinarData.thumbnailFile instanceof Blob
      
      let requestData
      
      if (hasThumbnailFile) {
        // Utiliser FormData pour l'upload de fichier
        const formData = new FormData()
        
        // Champs obligatoires
        if (webinarData.title) formData.append('title', webinarData.title)
        if (webinarData.description) formData.append('description', webinarData.description)
        if (webinarData.date) formData.append('date', webinarData.date)
        if (webinarData.meetLink) formData.append('meetLink', webinarData.meetLink)
        
        // Champs optionnels
        if (webinarData.videoLink) formData.append('videoLink', webinarData.videoLink)
        if (webinarData.duration) formData.append('duration', webinarData.duration.toString())
        if (webinarData.maxParticipants) formData.append('maxParticipants', webinarData.maxParticipants.toString())
        if (webinarData.language) formData.append('language', webinarData.language)
        // Gérer speakerIds[] (tableau)
        if (webinarData.speakerIds && Array.isArray(webinarData.speakerIds) && webinarData.speakerIds.length > 0) {
          webinarData.speakerIds.forEach(speakerId => {
            if (speakerId) formData.append('speakerIds[]', speakerId)
          })
        }
        formData.append('isPublished', webinarData.isPublished ? 'true' : 'false')
        
        // Fichier thumbnail
        if (hasThumbnailFile) {
          formData.append('thumbnail', webinarData.thumbnailFile)
        } else if (webinarData.thumbnail) {
          // Si c'est une URL, l'ajouter comme string
          formData.append('thumbnail', webinarData.thumbnail)
        }
        
        requestData = formData
      } else {
        // Utiliser JSON normal
        requestData = webinarData
      }
      
      const data = await api.post(endpoints.createWebinar, requestData)
      showSuccessToast({ message: 'Webinaire créé avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de la création du webinaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Modifier un webinaire
  const updateWebinar = useCallback(async (webinarId, webinarData) => {
    setLoading(true)
    try {
      // Vérifier si un fichier thumbnail est présent
      const hasThumbnailFile = webinarData.thumbnailFile instanceof File || webinarData.thumbnailFile instanceof Blob
      
      let requestData
      
      if (hasThumbnailFile) {
        // Utiliser FormData pour l'upload de fichier
        const formData = new FormData()
        
        // Ajouter tous les champs qui sont définis
        Object.keys(webinarData).forEach(key => {
          if (key === 'thumbnailFile') {
            // Ignorer thumbnailFile, on ajoutera thumbnail à la place
            return
          }
          if (key === 'thumbnail' && hasThumbnailFile) {
            // Ignorer thumbnail si on a un fichier
            return
          }
          
          const value = webinarData[key]
          if (value !== undefined && value !== null && value !== '') {
            // Gérer speakerIds[] comme un tableau
            if (key === 'speakerIds' && Array.isArray(value)) {
              value.forEach(speakerId => {
                if (speakerId) formData.append('speakerIds[]', speakerId)
              })
            } else if (typeof value === 'boolean') {
              formData.append(key, value ? 'true' : 'false')
            } else if (typeof value === 'number') {
              formData.append(key, value.toString())
            } else {
              formData.append(key, value)
            }
          }
        })
        
        // Fichier thumbnail
        if (hasThumbnailFile) {
          formData.append('thumbnail', webinarData.thumbnailFile)
        } else if (webinarData.thumbnail !== undefined) {
          // Si c'est une URL ou null, l'ajouter
          formData.append('thumbnail', webinarData.thumbnail || '')
        }
        
        requestData = formData
      } else {
        // Utiliser JSON normal
        requestData = webinarData
      }
      
      const data = await api.put(endpoints.updateWebinar(webinarId), requestData)
      showSuccessToast({ message: 'Webinaire modifié avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de la modification du webinaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Supprimer un webinaire
  const deleteWebinar = useCallback(async (webinarId) => {
    setLoading(true)
    try {
      await api.delete(endpoints.deleteWebinar(webinarId))
      showSuccessToast({ message: 'Webinaire supprimé avec succès' })
    } catch (error) {
      const message = error?.message || 'Erreur lors de la suppression du webinaire'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Lister tous les webinaires (admin - publiés et brouillons)
  const getAllWebinarsAdmin = useCallback(async (options = {}) => {
    const { status = 'ALL', page = 1, limit = 10 } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        status,
        page: page.toString(),
        limit: limit.toString()
      })
      const data = await api.get(`${endpoints.getAllWebinarsAdmin}?${params.toString()}`)
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement des webinaires'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Liste des inscrits à un webinaire (paginée)
  const getWebinarRegistrations = useCallback(async (webinarId, options = {}) => {
    const { page = 1, limit = 20 } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })
      const data = await api.get(`${endpoints.getWebinarRegistrations(webinarId)}?${params.toString()}`)
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement des inscriptions'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    // User/Public
    getWebinars,
    getWebinar,
    registerToWebinar,
    cancelRegistration,
    getMyRegistrations,
    // Admin
    createWebinar,
    updateWebinar,
    deleteWebinar,
    getAllWebinarsAdmin,
    getWebinarRegistrations
  }
}

